// Layer 3 (Transformations): the Maintenance landing (polish.P14 #701, locked
// comp `.impeccable/mocks/decision/landing-maintenance.html` A, finished with
// B's BM01 release-check clock and C's plant-area small caps). Pure: no React,
// no engine mutation, no clock read of its own.
//
// Every figure is read, never typed:
//   - the ledger and register from `MaintenanceTabs.transform.ts`; the work list
//     from `/maintenance`'s PM seed (`pm.seed.ts` → `PmSchedule.transform.ts`):
//     the landing keeps no work orders of its own (#734);
//   - PM compliance and the worst overdue PM from `/maintenance`'s own
//     register read (`strategyKpis`), shown only on the onward card
//     (adjudication 2);
//   - the planned ratio from Downtime's event log through
//     `plannedRatioMonthAt` — the month row Downtime prints (adjudication 1);
//   - MTTR from Downtime's class roll-up (`reliabilityKpis`), in its hours account;
//   - "N of M below MTBF target" from `mtbfChartRows`, where only categories
//     with a target count (adjudication 5);
//   - the BM01 stop from the GM's escalation card and its one record
//     (`escalationCards`, `decisionRecordLine`): the escalation engine records
//     once and has no confirm step, so this seat shows the state lines only
//     (adjudication 6).
// No money on this landing (adjudication 10).

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { EscalationDecisionRecord } from '@/versions/v1/shared/EscalationDecisionEngine';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { shiftOf } from '@/versions/v1/shared/clock.derive';
import { downtimeEvents } from '@/versions/v1/modules/maintenance/DowntimeEvents.transform';
import { PLANNED_RATIO_TARGET_PCT } from '@/versions/v1/modules/maintenance/downtime-events.seed';
import { BASIS_DAYS } from '@/versions/v1/modules/maintenance/downtime.seed';
import { pmTasks, type PmTaskRow } from '@/versions/v1/modules/maintenance/PmSchedule.transform';
import { conditionMonitoringPoints } from '@/versions/v1/modules/maintenance/Scene.transform';
import { assetRegisterRows, itemsByCriticality, strategyKpis } from '@/versions/v1/modules/maintenance/Strategy.transform';
import { plannedRatioMonthAt, plannedRatioMonths } from '@/versions/v1/modules/downtime/PlannedRatio.transform';
import { reliabilityKpis, reliabilityRows } from '@/versions/v1/modules/downtime/Reliability.transform';
import type { GmMiniItem, LandingStripCell } from './GmLeadBand.transform';
import { decisionRecordLine, escalationCards } from './GmTabs.transform';
import {
  capitalRegisterRows,
  equipmentHealthRows,
  maintScheduleRows,
  mtbfBarTone,
  mtbfChartRows,
  type EquipmentHealthRow,
  type TextSegment,
} from './MaintenanceTabs.transform';

export type MaintTabId = 'equipment' | 'workorders' | 'reliability';

export const MAINT_SUBTITLE = 'Plant equipment against its condition limits, MTBF and planned-work targets';

/** Safe read of a derivation that may not resolve at every cursor: the landing
 *  renders the rest and the region reads its honest state (P13 r1 lesson). */
function attempt<T>(read: () => T): T | null {
  try {
    return read();
  } catch {
    return null;
  }
}

function hm(at: SeriesTime): string {
  return at.slice(11, 16);
}

// ---- The ledger ----------------------------------------------------------------------

export type TrendWord = { word: 'Worsening' | 'Improving' | 'Stable' | 'Unmeasured'; tone: 'bad' | 'ok' | 'neutral' };

export type LedgerRow = {
  id: string;
  rank: number;
  area: string;
  name: string;
  cond: EquipmentHealthRow['cond'];
  alert?: string;
  now: readonly TextSegment[];
  nowSub?: string;
  next?: { text: string; past: boolean };
  trend: TrendWord;
};

export type Ledger = {
  /** Alerts, then every category off `ok`: the rows drawn on first paint. */
  first: readonly LedgerRow[];
  /** The categories in good condition, one click down. */
  rest: readonly LedgerRow[];
  /** The one-line head over `rest`: `3 more in good condition`. */
  restLabel: string;
};

const COND_RANK: Record<EquipmentHealthRow['cond'], number> = { bad: 0, warn: 1, ok: 2 };

export function trendWord(trend: EquipmentHealthRow['trend']): TrendWord {
  if (trend === 'worsening') return { word: 'Worsening', tone: 'bad' };
  if (trend === 'improving') return { word: 'Improving', tone: 'ok' };
  if (trend === 'unmeasured') return { word: 'Unmeasured', tone: 'neutral' };
  return { word: 'Stable', tone: 'neutral' };
}

/** The eight categories ranked alerts first, then condition (bad → warn → ok),
 *  seed order breaking ties. A next step whose own clock has passed reads
 *  greyed, never late (adjudication 8); the step is a condition action, so it
 *  carries no work-order id (#734). */
export function maintLedger(state: SessionState): Ledger {
  const rows = attempt(() => equipmentHealthRows(state)) ?? [];
  const ranked = rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) =>
      Number(Boolean(b.row.alert)) - Number(Boolean(a.row.alert)) ||
      COND_RANK[a.row.cond] - COND_RANK[b.row.cond] ||
      a.index - b.index)
    .map(({ row }, index): LedgerRow => {
      return {
        id: row.id,
        rank: index + 1,
        area: row.area,
        name: row.name,
        cond: row.cond,
        ...(row.alert ? { alert: row.alert.label } : {}),
        now: row.now,
        ...(row.nowSub ? { nowSub: row.nowSub } : {}),
        ...(row.next
          ? { next: { text: row.next.text, past: row.next.at !== undefined && row.next.at < state.cursor } }
          : {}),
        trend: trendWord(row.trend),
      };
    });
  const first = ranked.filter((row) => row.alert || row.cond !== 'ok');
  const rest = ranked.filter((row) => !first.includes(row));
  // `first` keeps every row off `ok` and every alert row, so `rest` is all-ok;
  // the guard stays so a future ranking change cannot mislabel the line.
  const allOk = rest.every((row) => row.cond === 'ok');
  return { first, rest, restLabel: `${rest.length} more${allOk ? ' in good condition' : ''}` };
}

// ---- The strip -----------------------------------------------------------------------

/** The PM compliance card: `/maintenance`'s own register read over its 30-day
 *  basis, and its worst overdue PM. Null when the register cannot be read at
 *  this cursor. */
export type PmCard = {
  figure: string;
  unit: string;
  lead: string;
  rest: string;
};

export function maintPmCard(state: SessionState): PmCard | null {
  return attempt(() => {
    const tasks = pmTasks();
    const points = conditionMonitoringPoints(state);
    const register = strategyKpis(assetRegisterRows(itemsByCriticality(), tasks, points, state.cursor), tasks, points, state.cursor);
    const { compliance, overdue } = register;
    const worst = overdue.worst;
    return {
      figure: compliance.value,
      unit: `${BASIS_DAYS} days`,
      lead: `${compliance.onTime} of ${compliance.due} on time`,
      rest: worst ? ` · ${worst.label.split(' ')[0]} PM ${worst.daysOverdue} day${worst.daysOverdue === 1 ? '' : 's'} overdue` : '',
    };
  });
}

/** The planned ratio for the cursor's month, as Downtime's Planned vs unplanned tab prints it. */
export function maintPlannedRatio(state: SessionState): { pct: number; label: string; month: string; targetPct: number } | null {
  const month = attempt(() => plannedRatioMonthAt(downtimeEvents(), state.cursor));
  if (!month) return null;
  return { pct: month.plannedPct, label: `${Math.round(month.plannedPct)}%`, month: month.label, targetPct: PLANNED_RATIO_TARGET_PCT };
}

/** Categories with an MTBF target, and how many sit below it. */
export function maintMtbfBelow(state: SessionState): { below: number; withTarget: number } {
  const rows = attempt(() => mtbfChartRows(state)) ?? [];
  return { below: rows.filter((row) => row.ratio < 1).length, withTarget: rows.length };
}

function technicians(rows: readonly PmTaskRow[]): string[] {
  const names: string[] = [];
  for (const row of rows) {
    if (!names.includes(row.crew)) names.push(row.crew);
  }
  return names;
}

export function maintStripCells(state: SessionState): LandingStripCell<MaintTabId>[] {
  const ledger = maintLedger(state);
  const all = [...ledger.first, ...ledger.rest];
  const alertShorts = (attempt(() => equipmentHealthRows(state)) ?? [])
    .filter((row) => row.alert)
    .map((row) => row.alert!.short);
  const schedule = maintScheduleRows(state);
  const dueToday = schedule.filter((row) => row.status === 'due-today');
  const overdue = schedule.filter((row) => row.overdue);
  const crew = technicians(dueToday);
  const mtbf = maintMtbfBelow(state);
  const ratio = maintPlannedRatio(state);
  return [
    {
      id: 'equipment',
      name: 'Equipment health',
      figure: String(alertShorts.length),
      figureContext: alertShorts.length === 1 ? 'critical alert' : 'critical alerts',
      comparisonLead: `of ${all.length} categories`,
      comparisonRest: alertShorts.length > 0 ? ` · ${alertShorts.join(' · ')}` : '',
      actor: 'System',
    },
    {
      id: 'workorders',
      name: 'Work orders',
      figure: String(dueToday.length),
      figureContext: 'due today',
      comparisonLead: `${overdue.length} overdue`,
      comparisonRest: overdue[0] ? ` · ${overdue[0].asset} · ${overdue[0].task}` : '',
      actor: 'Human',
      actorLabel: `${crew.length} technician${crew.length === 1 ? '' : 's'}`,
    },
    {
      id: 'reliability',
      name: 'Reliability',
      figure: `${mtbf.below} of ${mtbf.withTarget}`,
      figureContext: 'below MTBF target',
      comparisonLead: ratio ? 'planned ratio' : '',
      comparisonRest: ratio ? ` ${ratio.label} vs ${ratio.targetPct}% target · ${ratio.month}` : '',
      actor: 'Engine',
    },
  ];
}

// ---- Waiting on you --------------------------------------------------------------------

export type ReleaseCheckLine = { at: string; text: string; workOrderId?: string; past: boolean };

export type StopGate = {
  cardId: string;
  /** The GM has recorded a decision, so the gate eases to its approved surface. */
  recorded: boolean;
  question: { lead: string; rest: string };
  seats: readonly { role: string; holder: string }[];
  /** The state lines: the record and who still confirms, or who decides and by when. */
  state: { text: string; tone: 'ok' | 'neutral' }[];
};

export type WaitingItem = {
  id: 'bm01' | 'overdue' | 'capital' | 'fmea';
  title: string;
  due: { text: string; tone: 'bad' | 'neutral' };
  /** The line under the title; `strong` segments set in ink. */
  fin: readonly TextSegment[];
  caseLink?: { caseId: string; code: string };
  checks?: readonly ReleaseCheckLine[];
  gate?: StopGate;
  approvals?: boolean;
};

function bm01Item(state: SessionState, record: EscalationDecisionRecord | undefined): WaitingItem | null {
  const caseItem = CASES.find((row) => row.assignedTo === 'maintenance' && row.stopPlan?.stopDecision);
  const card = attempt(() => escalationCards(state).find((row) => row.id === 'esc-1'));
  if (!caseItem?.stopPlan || !card) return null;
  // The case exists on the landing once it has been raised at this cursor.
  if (caseItem.raisedAt && caseItem.raisedAt > state.cursor) return null;
  const plan = caseItem.stopPlan;
  const decision = plan.stopDecision!;
  const day = state.cursor.slice(0, 10);
  // The case's own work order — the draft stop's `WO-441`, the one
  // `/maintenance` draws on the bearing chart — sits on the first timed release
  // check, the 14:00 vibration check the lock comp names (there is no work order
  // for BM01 in the PM schedule: D16 keeps the bearing's stop out of the plan
  // until it is approved).
  const timedChecks = (plan.releaseChecks ?? []).filter(
    (check): check is typeof check & { at: string } => typeof check.at === 'string' && /^\d\d:\d\d$/.test(check.at),
  );
  const checks: ReleaseCheckLine[] = timedChecks.map((check, index) => {
    const dueAt = `${day}T${check.at}` as SeriesTime;
    return {
      at: check.at,
      text: `${check.lead}${check.detail}${check.basis ? ` ${check.basis}` : ''}`,
      ...(index === 0 && plan.workOrderId ? { workOrderId: plan.workOrderId } : {}),
      past: dueAt < state.cursor,
    };
  });
  const eod = decision.due.replace(/^today\s+/i, '');
  checks.push({ at: eod, text: `${decision.decides} decides · you confirm`, past: false });

  const line = record ? decisionRecordLine(card, record) : null;
  const gateState: StopGate['state'] = line
    ? [
        { text: `${line.recorded} · ${record!.decision}`, tone: 'ok' },
        ...(line.awaiting ? [{ text: line.awaiting, tone: 'neutral' as const }] : []),
      ]
    : [{ text: `Awaiting ${decision.decides} decision · ${decision.due}`, tone: 'neutral' }];
  const [lead, ...rest] = card.decision.split(' for ');
  const handler = caseItem.handler;
  return {
    id: 'bm01',
    title: card.title.split(' — ')[0] ?? card.title,
    due: caseItem.respondBy
      ? { text: `Respond by ${caseItem.respondBy.at}`, tone: 'bad' }
      : { text: decision.due, tone: 'neutral' },
    fin: [
      { text: 'Case ' },
      { text: caseItem.code ?? caseItem.id, code: true },
      ...(handler ? [{ text: ` · handler ${handler.name} since ${handler.since}` }] : []),
    ],
    caseLink: { caseId: caseItem.id, code: caseItem.code ?? caseItem.id },
    checks,
    gate: {
      cardId: card.id,
      recorded: line !== null,
      question: { lead: lead ?? card.decision, rest: rest.length > 0 ? ` for ${rest.join(' for ')}` : '' },
      seats: card.seats.map((seat) => ({ role: seat.role, holder: seat.holder })),
      state: gateState,
    },
  };
}

/** The lead's own gates, in order: the BM01 stop (the GM decides, this seat
 *  confirms), the overdue work order, the two business cases the GM expects,
 *  and the FMEA reviews under `fmea:review`. */
export function maintWaiting(
  state: SessionState,
  opts: { record?: EscalationDecisionRecord; canReview: boolean; pendingReviews: number },
): WaitingItem[] {
  const items: WaitingItem[] = [];
  const bm01 = bm01Item(state, opts.record);
  if (bm01) items.push(bm01);

  for (const row of maintScheduleRows(state).filter((w) => w.overdue)) {
    items.push({
      id: 'overdue',
      title: `${row.asset} · ${row.task}`,
      due: { text: `Overdue since ${row.due}`, tone: 'bad' },
      fin: [{ text: `${row.crew} · needs a date` }],
    });
  }

  const cards = attempt(() => escalationCards(state)) ?? [];
  const capital = cards.filter((card) => card.group === 'capital');
  const register = capitalRegisterRows(state);
  const cases = capital
    .map((card) => register.find((row) => card.title.includes(`(${row.id})`)))
    .filter((row): row is NonNullable<typeof row> => Boolean(row));
  if (cases.length > 0) {
    items.push({
      id: 'capital',
      title: `${cases.map((row) => row.id).join(' and ')} business case${cases.length === 1 ? '' : 's'}`,
      due: { text: `To GM · ${capital[0]!.due}`, tone: 'neutral' },
      fin: [{ text: [...cases.map((row) => row.title.replace(' — ', ' for the ')), ...(cases.length > 1 ? ['decided together'] : [])].join(' · ') }],
    });
  }

  if (opts.canReview && opts.pendingReviews > 0) {
    items.push({
      id: 'fmea',
      title: `${opts.pendingReviews} proposed FMEA change${opts.pendingReviews === 1 ? '' : 's'}`,
      due: { text: 'Your review', tone: 'neutral' },
      fin: [],
      approvals: true,
    });
  }
  return items;
}

// ---- Work orders ------------------------------------------------------------------------

export type WorkOrderLine = PmTaskRow & {
  /** The due cell's ink: an overdue row in the blocked tone, the rest in ink. */
  dueTone: 'bad' | 'ink';
  statusTone: 'bad' | 'warn' | 'neutral';
};

/**
 * The landing's work list, `/maintenance`'s PM schedule: one row per open PM
 * instance, in that route's own order (overdue first, then due date) and with
 * its hours. The status pill tone decodes the PM status — `Overdue` blocked,
 * `Due today` warning, `Scheduled` neutral — the same decode the PM schedule
 * tab's pills carry (#734).
 */
export function maintWorkOrders(state: SessionState): WorkOrderLine[] {
  return maintScheduleRows(state).map((row) => ({
    ...row,
    dueTone: row.overdue ? 'bad' : 'ink',
    statusTone: row.overdue ? 'bad' : row.status === 'due-today' ? 'warn' : 'neutral',
  }));
}

export type CrewCard = {
  window: string;
  bookedHours: number;
  technicianCount: number;
  averageHours: number;
  rows: { name: string; lead: string; more: readonly string[] }[];
};

/** The crews on today's PM work: the hours booked (the plan's `[syn]` estimates
 *  on the tasks due today), and each crew's tasks in due order. */
export function maintCrew(state: SessionState): CrewCard {
  const shift = shiftOf(state.cursor);
  const all = maintWorkOrders(state);
  const dueToday = all.filter((row) => row.status === 'due-today');
  const booked = dueToday.reduce((sum, row) => sum + row.hours, 0);
  const names = technicians(dueToday);
  const job = (row: PmTaskRow) => `${row.id} ${row.asset} · ${row.due}`;
  return {
    window: `${shift.kind} shift · ${hm(shift.start)}–${hm(shift.end)}`,
    bookedHours: booked,
    technicianCount: names.length,
    averageHours: names.length > 0 ? booked / names.length : 0,
    rows: names.map((name) => {
      const mine = all.filter((row) => row.crew === name);
      const today = mine.filter((row) => row.status === 'due-today');
      const others = mine.filter((row) => !today.includes(row));
      return {
        name,
        lead: today.map(job).join(' · '),
        more: others.map(job),
      };
    }),
  };
}

// ---- Reliability ------------------------------------------------------------------------

export type MtbfBar = {
  id: string;
  name: string;
  actual: number;
  target: number;
  pct: number;
  tone: 'ok' | 'warn' | 'bad';
  /** Bar and tick positions on one scale: the largest target. */
  barPct: number;
  tickPct: number;
};

export function maintMtbfBars(state: SessionState): MtbfBar[] {
  const rows = attempt(() => mtbfChartRows(state)) ?? [];
  const scale = rows.reduce((max, row) => Math.max(max, row.target, row.actual), 0);
  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    actual: row.actual,
    target: row.target,
    pct: Math.round(row.ratio * 100),
    tone: mtbfBarTone(row.ratio),
    barPct: scale > 0 ? (row.actual / scale) * 100 : 0,
    tickPct: scale > 0 ? (row.target / scale) * 100 : 0,
  }));
}

/** The register's rows as the landing draws them — no capex (adjudication 10);
 *  the investigations name when their business case goes to the GM. */
export function maintRegister(state: SessionState): { id: string; title: string; line: string; status: 'Investigation' | 'Approved'; meta: string }[] {
  const capitalDue = (attempt(() => escalationCards(state)) ?? []).find((card) => card.group === 'capital')?.due;
  return capitalRegisterRows(state).map((row) => ({
    id: row.id,
    title: row.title,
    line: row.businessCase,
    status: row.status,
    meta: row.payback ?? (capitalDue ? `Business case to GM · ${capitalDue}` : 'Business case to GM'),
  }));
}

/** The highest-frequency failure category — the lowest MTBF — and the register change that addresses it. */
export function maintInsight(state: SessionState): { headline: string; body: string; opportunity: { id: string; caption: string } | null } | null {
  const rows = (attempt(() => equipmentHealthRows(state)) ?? []).filter((row) => row.mtbfActual > 0);
  if (rows.length === 0) return null;
  const worst = rows.reduce((low, row) => (row.mtbfActual < low.mtbfActual ? row : low));
  const fix = capitalRegisterRows(state).find((row) => row.categoryId === worst.id);
  const title = fix?.title.split(' — ')[0];
  const failures = worst.failures;
  return {
    headline: `${worst.plainName} are the highest-frequency failure category`,
    body: [
      `${worst.mtbfActual} w MTBF vs ${worst.mtbfTarget} w target`,
      ...(failures ? [`${failures.ytd} ${failures.noun} YTD`] : []),
      ...(title ? [`the ${title} changes the payback calculation`] : []),
    ].join(' · '),
    opportunity: fix
      ? { id: fix.id, caption: [title, ...(failures ? [`${worst.plainName.toLowerCase().replace(/s$/, '')} ${failures.noun}`] : [])].join(' · ') }
      : null,
  };
}

/** The Reliability view's figure row: MTTR in Downtime's class account, the
 *  planned-ratio run below target, the top improvement opportunity. */
export function maintReliabilityBand(state: SessionState, links: { mtbf: string; ratio: string }): GmMiniItem[] {
  const events = attempt(() => downtimeEvents()) ?? [];
  const kpis = attempt(() => reliabilityKpis(reliabilityRows(events)));
  const months = attempt(() => plannedRatioMonths(events)) ?? [];
  let run = 0;
  for (let i = months.length - 1; i >= 0 && months[i]!.plannedPct < PLANNED_RATIO_TARGET_PCT; i -= 1) run += 1;
  const insight = maintInsight(state);
  const items: GmMiniItem[] = [
    kpis
      ? {
          id: 'mttr',
          label: 'MTTR on target',
          value: `${kpis.mttrWithinTargetCount} of ${kpis.mttrMeasuredCount}`,
          caption: `equipment classes · ${kpis.basisLabel}`,
          tone: kpis.mttrWithinTargetCount >= kpis.mttrMeasuredCount ? 'ok' : 'bad',
          link: { to: links.mtbf, label: 'Downtime' },
        }
      : { id: 'mttr', label: 'MTTR on target', value: 'unmeasured', caption: 'equipment classes', tone: 'neutral' },
    {
      id: 'ratio-trend',
      label: 'Planned ratio · trend',
      value: `${run} month${run === 1 ? '' : 's'}`,
      caption: `below the ${PLANNED_RATIO_TARGET_PCT}% target`,
      tone: (run > 0 ? 'bad' : 'ok') as VizTone,
      link: { to: links.ratio, label: 'Downtime' },
    },
  ];
  if (insight?.opportunity) {
    items.push({ id: 'opportunity', label: 'Top opportunity', value: insight.opportunity.id, caption: insight.opportunity.caption, tone: 'neutral' });
  }
  return items;
}
